import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { 
  Heart, Calendar, MapPin, Users, LogOut, ExternalLink, 
  Sparkles, Utensils, Clock, Plane
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Subtle background
const PremiumBackground = () => {
  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none">
      <div className="absolute inset-0 gradient-bg"></div>
      <div className="absolute inset-0 grid-pattern opacity-30"></div>
      <div className="glow-orb glow-orb-1"></div>
      <div className="glow-orb glow-orb-2"></div>
    </div>
  );
};

// Personality Meter Component
const PersonalityMeter = ({ personality }) => {
  if (!personality) return null;
  
  const traits = [
    { key: "adventurer", label: "Adventurer", emoji: "🚀", color: "personality-adventurer" },
    { key: "connector", label: "Connector", emoji: "💫", color: "personality-connector" },
    { key: "explorer", label: "Explorer", emoji: "🌍", color: "personality-explorer" },
    { key: "thinker", label: "Thinker", emoji: "💭", color: "personality-thinker" },
    { key: "creator", label: "Creator", emoji: "✨", color: "personality-creator" }
  ];

  return (
    <div className="space-y-3">
      {traits.map((trait) => (
        <div key={trait.key} className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1.5">
              <span>{trait.emoji}</span>
              <span className="text-[#9CA3AF]">{trait.label}</span>
            </div>
            <span className="text-[#A78BFA] font-medium">{personality[trait.key] || 0}%</span>
          </div>
          <div className="personality-bar">
            <div 
              className={`personality-bar-fill ${trait.color}`}
              style={{ width: `${personality[trait.key] || 0}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/dashboard`, { withCredentials: true });
      setDashboardData(response.data);

      if (!response.data.user?.onboarded) {
        navigate("/onboarding", { replace: true });
        return;
      }
    } catch (error) {
      console.error("Dashboard error:", error);
      if (error.response?.status === 401) {
        navigate("/", { replace: true });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success("See you soon!");
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/", { replace: true });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "TBD";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const vibeEmojis = {
    adventurer: "🚀",
    connector: "💫",
    explorer: "🌍",
    thinker: "💭",
    creator: "✨"
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#060814] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#A78BFA] flex items-center justify-center mb-4 glow-purple animate-pulse">
            <Users className="w-8 h-8 text-white" />
          </div>
          <p className="text-[#9CA3AF] font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const { pod, pod_members, events, next_meetup } = dashboardData || {};
  const currentUser = dashboardData?.user || user;

  return (
    <div className="min-h-screen bg-[#060814] text-[#F0F2F7] relative">
      <PremiumBackground />

      {/* Navigation - Modern Tech Style */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-nav">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] flex items-center justify-center">
                <Heart className="w-5 h-5 text-white" />
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-xl font-bold text-[#F0F2F7] leading-tight">
                heysolo<span className="text-[#A78BFA]">.ai</span>
              </span>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <Avatar className="w-10 h-10 border-2 border-[#A78BFA]/30">
                <AvatarImage src={currentUser?.picture} alt={currentUser?.name} />
                <AvatarFallback className="bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] text-white font-semibold">
                  {currentUser?.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:block">
                <span className="text-sm font-medium text-[#F0F2F7] block">
                  {currentUser?.display_name || currentUser?.name}
                </span>
                <span className="text-xs text-[#A78BFA]">
                  {vibeEmojis[currentUser?.vibe_type] || "✨"} {currentUser?.vibe_name || 'Discovering...'}
                </span>
              </div>
            </div>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-[#9CA3AF] hover:text-[#A78BFA] hover:bg-[#141B2D] rounded-full"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="h-px bg-gradient-to-r from-transparent via-[#A78BFA]/20 to-transparent"></div>
      </nav>

      {/* Main Content */}
      <main className="relative pt-28 pb-16 px-6 md:px-12 z-10">
        <div className="max-w-7xl mx-auto space-y-8">
          
          {/* Welcome Header */}
          <div className="space-y-2">
            <h1 
              className="text-3xl md:text-4xl font-bold text-[#F0F2F7]"
              data-testid="dashboard-welcome"
            >
              Welcome back, <span className="gradient-text-purple">{currentUser?.display_name || currentUser?.name?.split(' ')[0]}</span>
              <span className="ml-2 text-2xl">{vibeEmojis[currentUser?.vibe_type] || "✨"}</span>
            </h1>
            <p className="text-[#9CA3AF] text-lg">
              {currentUser?.vibe_name ? `You're ${currentUser?.vibe_name}` : 'Your vibe journey continues'}
            </p>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 auto-rows-[minmax(180px,auto)]">
            
            {/* Personality Meter Card */}
            <div 
              className="md:col-span-1 md:row-span-2 feature-card p-6"
              data-testid="personality-card"
            >
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-[#EC4899]" />
                <span className="text-sm uppercase tracking-widest text-[#EC4899] font-semibold">
                  Your Vibe
                </span>
              </div>
              
              {currentUser?.personality ? (
                <PersonalityMeter personality={currentUser.personality} />
              ) : (
                <div className="text-center py-8">
                  <p className="text-[#6B7280]">Complete the survey to see your vibe!</p>
                </div>
              )}
            </div>

            {/* Next Meetup - Large Card */}
            <div 
              className="md:col-span-2 md:row-span-2 feature-card p-8 flex flex-col"
              data-testid="next-meetup-card"
            >
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-[#A78BFA]" />
                <span className="text-sm uppercase tracking-widest text-[#A78BFA] font-semibold">
                  Your Next Meetup
                </span>
              </div>
              
              <div className="flex-1 space-y-4">
                <h2 className="text-2xl md:text-3xl font-bold text-[#F0F2F7]">
                  Sunday with Your Tribe
                </h2>
                
                {next_meetup ? (
                  <>
                    <div className="flex items-center gap-3 text-[#9CA3AF]">
                      <Clock className="w-5 h-5 text-[#EC4899]" />
                      <span className="font-medium">{formatDate(next_meetup)}</span>
                    </div>
                    
                    <p className="text-[#9CA3AF]">
                      {pod?.vibe_theme || "Get ready for meaningful connections!"}
                    </p>
                    
                    <div className="flex gap-3 pt-2">
                      <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#EC4899]/10 border border-[#EC4899]/30">
                        <Utensils className="w-4 h-4 text-[#EC4899]" />
                        <span className="text-sm text-[#EC4899]">Dine</span>
                      </div>
                      <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#A78BFA]/10 border border-[#A78BFA]/30">
                        <Plane className="w-4 h-4 text-[#A78BFA]" />
                        <span className="text-sm text-[#A78BFA]">Travel</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-[#9CA3AF]">
                    Your tribe is being formed! Our AI is matching you with compatible vibes.
                  </p>
                )}
              </div>

              <Button
                data-testid="book-event-btn"
                className="mt-6 w-full rounded-full py-6 text-lg font-semibold btn-primary text-white border-0"
                onClick={() => toast.info("Booking link will be available soon!")}
              >
                Book Your Spot
                <ExternalLink className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Pod Info */}
            <div 
              className="md:col-span-1 feature-card p-6 space-y-4"
              data-testid="pod-info-card"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#EC4899]" />
                  <span className="text-sm uppercase tracking-widest text-[#EC4899] font-semibold">
                    Your Pod
                  </span>
                </div>
                {pod && (
                  <Badge className="badge-women rounded-full px-3 py-1 text-xs">
                    {pod_members?.length || 0}
                  </Badge>
                )}
              </div>

              {pod ? (
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-[#F0F2F7]">
                    {pod.name}
                  </h3>
                  <p className="text-xs text-[#9CA3AF]">{pod.vibe_theme}</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-[#F0F2F7]">
                    Finding Your Tribe...
                  </h3>
                  <p className="text-xs text-[#9CA3AF]">
                    AI matching in progress
                  </p>
                </div>
              )}
            </div>

            {/* Tribe Members */}
            <div 
              className="md:col-span-3 feature-card p-6 space-y-4"
              data-testid="sistahs-card"
            >
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-[#A78BFA]" />
                <span className="text-sm uppercase tracking-widest text-[#A78BFA] font-semibold">
                  Your Tribe
                </span>
              </div>

              {pod_members && pod_members.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {pod_members.slice(0, 7).map((member, index) => (
                    <div 
                      key={member.user_id}
                      className="flex flex-col items-center p-4 rounded-2xl bg-[#0D1225]/50 hover:bg-[#A78BFA]/10 transition-all duration-300 border border-[#1F2937]/50 hover:border-[#A78BFA]/30 group"
                      data-testid={`tribe-member-${index}`}
                    >
                      <Avatar className="w-14 h-14 border-2 border-[#1F2937] group-hover:border-[#A78BFA]/50 mb-2">
                        <AvatarImage src={member.picture} alt={member.display_name} />
                        <AvatarFallback className="bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] text-white text-sm font-semibold">
                          {member.display_name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm font-medium text-[#F0F2F7] text-center truncate w-full">
                        {member.display_name?.split(' ')[0] || 'Member'}
                      </span>
                      <div className="flex items-center gap-1 text-xs text-[#6B7280]">
                        <span>{vibeEmojis[member.vibe_type] || "✨"}</span>
                        <span className="text-[#A78BFA]">{member.vibe_name?.split(' ')[1] || ''}</span>
                      </div>
                      {member.personality && (
                        <div className="flex gap-0.5 mt-2 w-full justify-center">
                          {['adventurer', 'connector', 'explorer', 'thinker', 'creator'].map((trait) => (
                            <div 
                              key={trait}
                              className={`h-1 rounded-full personality-${trait}`}
                              style={{ 
                                width: `${Math.max(4, (member.personality[trait] || 0) / 6)}px`,
                                opacity: 0.4 + (member.personality[trait] || 0) / 200
                              }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto rounded-xl bg-gradient-to-br from-[#A78BFA]/20 to-[#EC4899]/20 flex items-center justify-center mb-4 border border-[#A78BFA]/20">
                    <Sparkles className="w-8 h-8 text-[#A78BFA]" />
                  </div>
                  <p className="text-[#9CA3AF]">Your tribe will appear here once matched!</p>
                </div>
              )}
            </div>

            {/* Upcoming Events */}
            <div 
              className="md:col-span-4 feature-card p-6 space-y-6"
              data-testid="events-card"
            >
              <div className="flex items-center gap-2">
                <Utensils className="w-5 h-5 text-[#EC4899]" />
                <span className="text-sm uppercase tracking-widest text-[#EC4899] font-semibold">
                  Upcoming Experiences
                </span>
              </div>

              {events && events.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {events.slice(0, 3).map((event, index) => (
                    <div 
                      key={event.event_id}
                      className="group rounded-2xl overflow-hidden bg-[#0D1225]/50 border border-[#1F2937]/50 hover:border-[#A78BFA]/30 transition-all duration-300"
                      data-testid={`dashboard-event-${index}`}
                    >
                      <div className="relative h-36 overflow-hidden">
                        <img 
                          src={event.image_url}
                          alt={event.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#060814] via-transparent to-transparent"></div>
                        <div className="absolute bottom-3 left-3 flex items-center gap-1.5 glass rounded-full px-3 py-1.5 text-xs">
                          <Calendar className="w-3.5 h-3.5 text-[#A78BFA]" />
                          <span className="text-[#F0F2F7] font-medium">
                            {new Date(event.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                      </div>
                      <div className="p-4 space-y-2">
                        <h4 className="font-semibold text-[#F0F2F7] line-clamp-1 group-hover:text-[#A78BFA] transition-colors">{event.title}</h4>
                        <div className="flex items-center gap-1.5 text-xs text-[#6B7280]">
                          <MapPin className="w-3.5 h-3.5 text-[#EC4899]" />
                          {event.location.split(',')[0]}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-[#6B7280] py-8">
                  Exciting events coming soon! Stay tuned.
                </p>
              )}
            </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
